type ScopeTagOptionSet = {
  Options: Array<{
    Value: number
    Label: {
      UserLocalizedLabel: {
        Label: string
      }
    }
  }>
}

export function getOtherScopeTagValue(systemScopeTagOptions?: ScopeTagOptionSet | null) {
  if (!systemScopeTagOptions) return null

  const otherOption = systemScopeTagOptions.Options.find(
    (option) => option.Label.UserLocalizedLabel.Label.split(' ').at(1)?.toLowerCase() === 'other'
  )

  return otherOption?.Value ?? null
}

type BucketedDetail = {
  dsy_cpqfields: {
    dsy_globalid?: string | null
    dsy_parentglobalid?: string | null
    dsy_packageglobalid?: string | null
    dsy_scopetag?: number | null
  }
}

export function indexDetailsByGlobalId<T extends BucketedDetail>(details: readonly T[] | null | undefined) {
  const byGlobalId = new Map<string, T>()
  for (const detail of details ?? []) {
    const globalId = detail.dsy_cpqfields.dsy_globalid
    if (globalId) byGlobalId.set(globalId, detail)
  }
  return byGlobalId
}

/**
 * The scope tag a row is SHOWN under. An accessory sits with the product it
 * hangs on and a package member with its header (`hasInheritedBucket`), so the
 * section is the parent's, resolved up the chain, never the row's own stored
 * value. Rows with no resolvable parent keep their own tag. Bounded so a
 * cyclic or dangling key can never loop.
 */
export function getEffectiveScopeTag(
  detail: BucketedDetail,
  detailsByGlobalId: ReadonlyMap<string, BucketedDetail>
): number | null | undefined {
  let current: BucketedDetail = detail
  const visited = new Set<BucketedDetail>([current])
  for (let depth = 0; depth < 8; depth++) {
    const { dsy_parentglobalid, dsy_packageglobalid } = current.dsy_cpqfields
    const parentKey = dsy_parentglobalid || dsy_packageglobalid
    if (!parentKey) break
    const parent = detailsByGlobalId.get(parentKey)
    if (!parent || visited.has(parent)) break
    visited.add(parent)
    current = parent
  }
  return current.dsy_cpqfields.dsy_scopetag
}

/**
 * Matches a detail's scope tag against a bucket scope tag. The "Other" bucket also
 * catches any detail whose scope tag is blank or outside the system's valid set.
 */
export function matchesScopeTagBucket(
  detailScopeTag: number | null | undefined,
  bucketScopeTag: number,
  systemScopeTagOptions: ScopeTagOptionSet
) {
  if (detailScopeTag === bucketScopeTag) return true
  const otherScopeTagValue = getOtherScopeTagValue(systemScopeTagOptions)
  if (bucketScopeTag !== otherScopeTagValue) return false
  if (detailScopeTag == null) return true
  return !systemScopeTagOptions.Options.some((opt) => opt.Value === detailScopeTag)
}
